import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { baseURL, requestMiddleware, headers } from '@/v2/constants/requests'
import {
  toFormKeys,
  normalizeOptions,
} from '@/v2/containers/health-history-form/utils'
import {
  GENDER_OPTIONS,
  DENTAL_CONDITIONS,
  MEDICAL_ISSUES,
} from '@/v2/containers/health-history-form/constants'

const OPTION_FALLBACKS = {
  genders: GENDER_OPTIONS,
  dental_conditions: DENTAL_CONDITIONS,
  medical_issues: MEDICAL_ISSUES,
}

// GET /v2/rest/intake_forms/:uuid.json — the clinic's intake setting, the form
// status, the clinic contact block and the option catalogs.
// See docs/v2/public/rest/intake_forms_doc.md.
export const useGetPublicIntakeForm = uuid => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['PublicIntakeForm', `${uuid}`],
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/intake_forms/${uuid}.json`)
      const body = await res.json().catch(() => ({}))

      // 410 Gone means the clinic disabled the form or it was already
      // completed. That is a screen we render, not a failure — resolve it so
      // the container can show the expired notice with the clinic's contact.
      if (res.status === 410) {
        return { expired: true, clinic: body.clinic }
      }

      if (!res.ok) {
        throw new Error(
          body.error || "We couldn't load the health history form"
        )
      }

      return body
    },
    enabled: !!uuid,
    // A 404/500 here is terminal (bad or foreign uuid); retrying just delays
    // the error screen.
    retry: false,
  })

  // Re-key once per fetch: these objects are memo dependencies in the
  // container, so rebuilding them every render would defeat those memos.
  const normalized = useMemo(
    () => ({
      form: toFormKeys(data?.intakeForm),
      setting: toFormKeys(data?.setting),
      clinic: toFormKeys(data?.clinic),
      options: normalizeOptions(data?.options, OPTION_FALLBACKS),
      isExpired: !!data?.expired,
      isLoaded: !!data,
    }),
    [data]
  )

  return { ...normalized, isLoading, isError }
}

// POST /v2/rest/intake_forms/:uuid/responses.json — rate limited to 10 per IP
// per minute.
export const useSubmitIntakeResponse = uuid => {
  const { mutateAsync: submitResponse, ...others } = requestMiddleware({
    key: ['SubmitIntakeResponse', `${uuid}`],
    request: async payload =>
      await fetch(`${baseURL}/v2/rest/intake_forms/${uuid}/responses.json`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      }),
  })

  return { submitResponse, ...others }
}
